import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { screenshotAPI } from '../lib/api';
import { 
  Upload, 
  FileImage,
  Trash2,
  Calendar,
  CheckCircle
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { formatDate } from '../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { Toaster, toast } from 'sonner';
import ArtistLayout from '../components/layout/ArtistLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ArtistPortal = () => {
  const { user } = useAuth();
  const [screenshots, setScreenshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [description, setDescription] = useState('');

  const fetchScreenshots = useCallback(async () => {
    try {
      const response = await screenshotAPI.getAll();
      setScreenshots(response.data);
    } catch (error) {
      console.error('Error fetching screenshots:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchScreenshots();
  }, [fetchScreenshots]);

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} is not an image`);
          continue;
        }

        await screenshotAPI.upload(file, description);
        toast.success(`${file.name} uploaded successfully`);
      }
      setDescription('');
      fetchScreenshots();
    } catch (error) {
      console.error('Error uploading:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload screenshot');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this screenshot?')) return;

    try {
      await screenshotAPI.delete(id);
      toast.success('Screenshot deleted');
      fetchScreenshots();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete screenshot');
    }
  };

  return (
    <ArtistLayout>
      <div className="space-y-6" data-testid="artist-portal-page">
        <Toaster position="top-right" richColors />

        {/* Upload Zone */}
        <Card>
          <CardContent className="p-6">
            <div
              className={`upload-zone ${dragOver ? 'dragover' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => document.getElementById('artist-file-input').click()}
            >
              <input
                id="artist-file-input"
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handleFileUpload(Array.from(e.target.files))}
                data-testid="artist-file-input"
              />
              {uploading ? (
                <div className="flex flex-col items-center">
                  <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full mb-3" />
                  <p className="text-slate-600">Uploading...</p>
                </div>
              ) : (
                <>
                  <div className="w-16 h-16 rounded-full bg-[#331062]/10 flex items-center justify-center mx-auto mb-4">
                    <Upload className="w-8 h-8 text-[#331062]" />
                  </div>
                  <p className="text-lg font-medium text-slate-700">
                    Drop your Teams screenshots here
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    or click to browse files
                  </p>
                  <p className="text-xs text-slate-400 mt-4">
                    PNG, JPG, JPEG • Max 10MB per file
                  </p>
                </>
              )}
            </div>
            <div className="mt-4">
              <Input
                placeholder="Add a description (optional) - e.g., 'Banner approval from client X'"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                data-testid="artist-description-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Recent Uploads */}
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Your Uploads</h3>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full" />
            </div>
          ) : screenshots.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileImage className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-800">No uploads yet</h3>
                <p className="text-slate-500 mt-1">
                  Your uploaded screenshots will appear here
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <AnimatePresence>
                {screenshots.map((screenshot, index) => (
                  <motion.div
                    key={screenshot.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="overflow-hidden" data-testid={`artist-screenshot-${screenshot.id}`}>
                      <div className="flex">
                        <div className="w-32 h-24 bg-slate-100 flex-shrink-0">
                          <img
                            src={`${BACKEND_URL}/api/screenshots/file/${screenshot.filename}`}
                            alt={screenshot.original_filename}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="128" height="96"><rect fill="%23f1f5f9" width="128" height="96"/><text x="50%" y="50%" fill="%2394a3b8" text-anchor="middle" dy=".3em" font-family="sans-serif" font-size="10">Error</text></svg>';
                            }}
                          />
                        </div>
                        <CardContent className="flex-1 p-3 flex flex-col justify-between">
                          <div>
                            <p className="font-medium text-slate-800 text-sm truncate">
                              {screenshot.original_filename}
                            </p>
                            {screenshot.description && (
                              <p className="text-xs text-slate-500 mt-1 line-clamp-1">
                                {screenshot.description}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(screenshot.uploaded_at)}
                            </span>
                            <button
                              onClick={() => handleDelete(screenshot.id)}
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              data-testid={`artist-delete-${screenshot.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </CardContent>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Info Card */}
        <Card className="bg-[#331062]/5 border-[#331062]/10">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-[#331062]/10 rounded-lg">
                <CheckCircle className="w-5 h-5 text-[#331062]" />
              </div>
              <div>
                <h4 className="font-medium text-slate-800">Upload Guidelines</h4>
                <ul className="text-sm text-slate-600 mt-2 space-y-1">
                  <li>• Upload clear screenshots from Microsoft Teams</li>
                  <li>• Include stakeholder approval messages when possible</li>
                  <li>• Add descriptions to help managers identify the content</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </ArtistLayout>
  );
};

export default ArtistPortal;
